import React from 'react';
import { Shield, FileText, Database, Home, Plane, Activity, Settings, Globe, Maximize2 } from 'lucide-react';
import InfoRow from './InfoRow';
import PillList from './PillList';
import CardEditRow from './CardEditRow';
import { formatCaseType } from '../../utils/listingTransformUtils';
import htmlToPlainText from '../../utils/htmlToPlainText';
import { getListingLinks } from '../../utils/listingDetails';

const dash = '–';

// onEditRow(key) is the listing card-builder seam (keys: perks, cases, skills,
// careteam, emr, website). Absent on every viewer surface, where this face
// renders exactly as it always has.
//
// hideEmptyRows mirrors the prop CRNAProfileCard threads into ProfileBack: a row
// with nothing in it is omitted rather than rendered as a dash. The rows that
// already gate themselves on having content (skills, care team, website) need
// nothing; the two that fall back to a dash placeholder are the ones this
// reaches. Default FALSE, so nothing that does not opt in moves.
const ListingBack = ({ profile = {}, isCertified = false, onEditRow = null, hideEmptyRows = false, onOpenDetails = null }) => {
  const builder = typeof onEditRow === 'function';
  const edit = (key) => (builder ? () => onEditRow(key) : null);
  const requiredCaseTypes = (profile.clinicalExpectations?.requiredCaseTypes?.length
    ? profile.clinicalExpectations.requiredCaseTypes.map(formatCaseType)
    : [dash]);
  const emrSystem = profile.clinicalExpectations?.emrSystem ?? dash;
  const website = profile.website || dash;
  // Both fall back to a dash rather than to nothing, so "is there a value here"
  // has to be asked explicitly. These are the same tests the rows' own `empty`
  // flags already make for builder mode, read the other way round.
  const hasCaseTypes = requiredCaseTypes.length > 0 && requiredCaseTypes[0] !== dash;
  const hasEmrSystem = Boolean(emrSystem) && emrSystem !== dash;
  // The employer's own words about the role. Clamped by CSS rather than
  // truncated here, so the full text is still in the DOM (and still there for a
  // provider who opens the listing detail page) but can never blow out a card
  // whose height is fixed.
  // Feed-sourced listings carry raw HTML in `description` -- these ~30 job
  // sources publish `<p>`, `<li>`, `&amp;` and `&#39;` -- and this row rendered
  // that string as text, so a provider was shown the markup instead of the
  // prose. htmlToPlainText converts it back to sentences, keeping paragraph and
  // bullet boundaries as newlines (.listing-backdesc is `white-space: pre-line`,
  // so they read as breaks rather than collapsing into one block).
  //
  // Its output is TEXT and is interpolated as text below. It must never reach
  // dangerouslySetInnerHTML: the util is an extractor, not a sanitiser, and
  // these sources are untrusted, so treating it as markup would make this a
  // stored-XSS surface on a provider-facing card.
  // The role line, which leads this face. It sits with the prose it introduces
  // rather than on the front, where it repeated the rate pill directly beneath
  // it and pushed the at-a-glance facts down a card of fixed height.
  // transformListingData folds an internal listing's `title` into
  // briefDescription, so both are read.
  const roleTitle = profile.title || profile.briefDescription || '';
  const roleDescription = htmlToPlainText(profile.fullDescription || profile.description);
  const hasListingLinks = getListingLinks(profile).length > 0;

  // Perks information moved from front to back
  const includesHousing = profile.compensation?.includesHousing ?? profile.includesHousing;
  const includesTravel = profile.compensation?.includesTravel ?? profile.includesTravel;
  const malpracticeCovered = profile.compensation?.malpracticeCovered ?? profile.malpracticeCovered;
  const benefitLabel = (value, yes, no, name) => value === true ? yes : value === false ? no : `${name}: not specified`;

  // Get required skills
  const skillsRequired = profile.clinical?.skillsRequired || profile.clinicalExpectations?.skillsRequired || {};
  const hasSkills = skillsRequired.ultrasoundBlocks || skillsRequired.epidurals || skillsRequired.spinals || skillsRequired.centralLines;

  // Build skills display array
  const skillsArray = [];
  if (skillsRequired.ultrasoundBlocks) skillsArray.push('Blocks');
  if (skillsRequired.epidurals) skillsArray.push('Epidurals');
  if (skillsRequired.spinals) skillsArray.push('Spinals');
  if (skillsRequired.centralLines) skillsArray.push('Central Lines');

  // Care team model from clinical data
  const careTeamModel = profile.clinical?.careTeamModel || [];
  const hasCareTeamModel = Array.isArray(careTeamModel) && careTeamModel.length > 0;

  // Credentialing data (passed via profile from ListingPreview)
  const credentialing = profile._credentialing;
  const hasCredentialing = credentialing && credentialing.enabled && credentialing.requiredCount > 0;

  // Progress ring values
  const totalRequired = credentialing?.requiredCount || 0;
  const docsReady = credentialing?.docsReady ?? null; // null = company view (no provider), number = provider view
  const isProviderView = docsReady !== null;
  const progress = isProviderView && totalRequired > 0 ? docsReady / totalRequired : null;

  // SVG ring constants
  const ringSize = 44;
  const strokeWidth = 3.5;
  const radius = (ringSize - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const progressOffset = progress !== null ? circumference * (1 - progress) : circumference;

  // Ring color based on progress
  const getRingColor = () => {
    if (!isProviderView) return '#6366f1'; // indigo for company view
    if (progress >= 1) return '#10b981'; // green = complete
    if (progress >= 0.7) return '#f59e0b'; // amber = almost there
    return '#ef4444'; // red = needs work
  };

  return (
    <div className="card-face-content listing-gradient-bg" role="region" aria-label="Listing Back">
      <div className="card-header">
        {hasCredentialing && (
          <div
            className="cred-ring-badge"
            title={isProviderView
              ? `${docsReady} of ${totalRequired} credentialing documents ready`
              : `${totalRequired} credentialing documents required`
            }
          >
            <svg className="cred-ring-svg" width={ringSize} height={ringSize} viewBox={`0 0 ${ringSize} ${ringSize}`}>
              {/* Background ring */}
              <circle
                cx={ringSize / 2} cy={ringSize / 2} r={radius}
                fill="none" stroke="#e5e7eb" strokeWidth={strokeWidth}
              />
              {/* Progress ring */}
              <circle
                cx={ringSize / 2} cy={ringSize / 2} r={radius}
                fill="none" stroke={getRingColor()} strokeWidth={strokeWidth}
                strokeDasharray={circumference}
                strokeDashoffset={progressOffset}
                strokeLinecap="round"
                transform={`rotate(-90 ${ringSize / 2} ${ringSize / 2})`}
                style={{ transition: 'stroke-dashoffset 0.5s ease' }}
              />
              {/* Center text */}
              <text x="50%" y="50%" textAnchor="middle" dominantBaseline="central"
                className="cred-ring-text" fill={getRingColor()}
              >
                {isProviderView ? docsReady : totalRequired}
              </text>
            </svg>
            <div className="cred-ring-label">
              {isProviderView ? (
                <>
                  <span className="cred-ring-title">{docsReady}/{totalRequired} Ready</span>
                  <span className="cred-ring-sub">Credentialing docs</span>
                </>
              ) : (
                <>
                  <span className="cred-ring-title">{totalRequired} Required</span>
                  <span className="cred-ring-sub">Credentialing docs</span>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* The role title, above the prose it introduces. Still keyed 'facility',
          so the employers builder opens the sheet it always did, just from this
          face now. */}
      {(roleTitle || builder) && (
        <CardEditRow
          onEdit={edit('facility')}
          label="position title"
          empty={!roleTitle}
          prompt="Add a position title"
          className="listing-roleline"
        >
          <p className="listing-roleline__text">{roleTitle}</p>
        </CardEditRow>
      )}

      {/* The role description, when there is one. It has its own sheet key so a
          host can LEAD a new card with it: the signed-in listings editor does,
          because a pasted posting is what fills the other rows. Where a host
          has not opted in, ListingFactSheet treats 'description' as an alias
          for the role sheet, which is what this row opened before the key
          existed. */}
      {(roleDescription || builder) && (
        <CardEditRow
          onEdit={edit('description')}
          label="position description"
          Icon={FileText}
          empty={!roleDescription}
          prompt="Add a position description"
        >
          {!builder && onOpenDetails ? (
            <button
              type="button"
              className="listing-description-trigger"
              onClick={(event) => {
                event.stopPropagation();
                onOpenDetails();
              }}
              aria-label="Open the complete listing description and links"
            >
              <p className="listing-backdesc">{roleDescription}</p>
              <span className="listing-description-trigger__cta">
                Full description &amp; links
                <Maximize2 size={14} aria-hidden="true" />
              </span>
            </button>
          ) : (
            <p className="listing-backdesc">{roleDescription}</p>
          )}
        </CardEditRow>
      )}

      {/* Perks now on the back. All three are one decision to the employer
          ("what do we cover?"), so in builder mode they share one hit area and
          one sheet rather than three rows that each open the same thing. */}
      <CardEditRow onEdit={edit('perks')} label="what you cover">
        <InfoRow Icon={Home}>
          <PillList
            items={[benefitLabel(includesHousing, 'Housing', 'No Housing', 'Housing')]}
            getTooltip={() => benefitLabel(includesHousing, 'Housing provided', 'Housing not provided', 'Housing')}
            idPrefix="housing"
          />
        </InfoRow>

        <InfoRow Icon={Plane}>
          <PillList
            items={[benefitLabel(includesTravel, 'Travel', 'No Travel', 'Travel')]}
            getTooltip={() => benefitLabel(includesTravel, 'Travel provided', 'Travel not provided', 'Travel')}
            idPrefix="travel"
          />
        </InfoRow>

        <InfoRow Icon={Shield}>
          <PillList
            items={[benefitLabel(malpracticeCovered, 'Malpractice', 'No Malpractice', 'Malpractice')]}
            getTooltip={() => benefitLabel(malpracticeCovered, 'Malpractice insurance provided', 'Malpractice insurance not provided', 'Malpractice')}
            idPrefix="malpractice"
          />
        </InfoRow>
      </CardEditRow>

      {(!hideEmptyRows || hasCaseTypes) && (
        <CardEditRow
          onEdit={edit('cases')}
          label="case types"
          Icon={FileText}
          empty={!hasCaseTypes}
          prompt="Add the case types"
        >
          <InfoRow Icon={FileText}>
            <PillList
              items={requiredCaseTypes}
              getTooltip={() => 'Required clinical case types'}
              idPrefix="case-type"
            />
          </InfoRow>
        </CardEditRow>
      )}

      {(hasSkills || builder) && (
        <CardEditRow
          onEdit={edit('skills')}
          label="required skills"
          Icon={Activity}
          empty={!hasSkills}
          prompt="Add required skills"
        >
          <InfoRow Icon={Activity}>
            <PillList
              items={skillsArray}
              getTooltip={skill => `Required skill: ${skill}`}
              idPrefix="skill"
            />
          </InfoRow>
        </CardEditRow>
      )}

      {/* Care Team Model */}
      {(hasCareTeamModel || builder) && (
        <CardEditRow
          onEdit={edit('careteam')}
          label="care team model"
          Icon={Settings}
          empty={!hasCareTeamModel}
          prompt="Add the care team model"
        >
          <InfoRow Icon={Settings}>
            <PillList
              items={careTeamModel}
              getTooltip={model => `Care team model: ${model}`}
              idPrefix="care-model"
            />
          </InfoRow>
        </CardEditRow>
      )}

      {(!hideEmptyRows || hasEmrSystem) && (
        <CardEditRow
          onEdit={edit('emr')}
          label="EMR system"
          Icon={Database}
          empty={!hasEmrSystem}
          prompt="Add the EMR system"
        >
          <InfoRow Icon={Database}>
            <PillList
              items={[emrSystem]}
              getTooltip={() => 'EMR system used at this facility'}
              idPrefix="emr"
            />
          </InfoRow>
        </CardEditRow>
      )}

      {!builder && !roleDescription && hasListingLinks && onOpenDetails && (
        <div className="website-container">
          <button
            type="button"
            className="website-link"
            onClick={(event) => {
              event.stopPropagation();
              onOpenDetails();
            }}
          >
            View Listing Links
          </button>
        </div>
      )}

      {/* Builder mode swaps the outbound link for a tap target: following a
          link out of a card you are mid-way through building loses the build. */}
      {builder && (
        <CardEditRow
          onEdit={edit('website')}
          label="facility website"
          Icon={Globe}
          empty={website === dash}
          prompt="Add your facility website"
        >
          <InfoRow Icon={Globe}>
            <PillList
              items={[website]}
              getTooltip={() => 'Facility website'}
              idPrefix="website"
            />
          </InfoRow>
        </CardEditRow>
      )}
    </div>
  );
};

export default ListingBack;
